import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from './_app';
import Head from 'next/head';
import Layout from '@/layouts';
import AboutUsContent from '@/components/Policy/AboutUs';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { canonicalUrl } from '@/shared/utils';
import { page2JsonLd } from '@/lib/jsonld';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;
  const localeRes = await serverSideTranslations(locale, ['common']);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;
  const jsonLd = page2JsonLd(
    {
      title: tempData['head_Meta_Title'],
      desc: tempData['head_Meta_Desc'],
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      url: siteUrl,
    }
  );
  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      title: tempData['head_Meta_Title'],
      description: tempData['head_Meta_Desc'],
      jsonLd,
    },
  };
};

const AboutUsPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = () => {
  return <AboutUsContent />;
};

AboutUsPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default AboutUsPage;
